import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useGetCreditAssessment, useGetCreditAssessmentReport } from '../../services/loanService';
import CreditAssessmentReportView from './CreditAssessmentReportView';
import { formatCurrency, formatDateTime } from '../../utils/formatters';

/**
 * What credit concluded, for everyone who looks at the application or the loan afterwards.
 *
 * The assessment notes were readable only on the credit assessment screen, by whoever held the
 * task, so the reasoning behind a loan was lost once it moved on.
 *
 * Read-only by design, like VerificationSummary. Renders nothing until an assessment exists: the
 * API answers 400 for an application that has not been assessed, which here is simply "not yet".
 */

interface CreditAssessmentSummaryProps {
  applicationId?: string;
}

const RECOMMENDATION_STYLES: Record<string, { label: string; className: string }> = {
  APPROVE: { label: 'Approve', className: 'bg-success-subtle text-success-emphasis' },
  APPROVE_WITH_CONDITIONS: { label: 'Approve with conditions', className: 'bg-info-subtle text-info-emphasis' },
  REFER: { label: 'Refer', className: 'bg-warning-subtle text-warning-emphasis' },
  REJECT: { label: 'Reject', className: 'bg-danger-subtle text-danger-emphasis' }
};

const RISK_STYLES: Record<string, { label: string; className: string }> = {
  LOW: { label: 'Low risk', className: 'bg-success-subtle text-success-emphasis' },
  MEDIUM: { label: 'Medium risk', className: 'bg-warning-subtle text-warning-emphasis' },
  HIGH: { label: 'High risk', className: 'bg-danger-subtle text-danger-emphasis' },
  VERY_HIGH: { label: 'Very high risk', className: 'bg-danger text-white' }
};

const CreditAssessmentSummary: React.FC<CreditAssessmentSummaryProps> = ({ applicationId }) => {
  const { data: assessment, isLoading, isError } = useGetCreditAssessment(
    applicationId || '',
    { query: { enabled: !!applicationId, retry: false } }
  );

  // The saved Credit Assessment Report, opened read only from here (Pule, 29 Oct 2026)
  const { data: report } = useGetCreditAssessmentReport(applicationId || '', { query: { enabled: !!applicationId, retry: false } });
  const [showReport, setShowReport] = useState(false);

  if (!applicationId || isLoading || isError || !assessment) return null;

  const details = assessment.assessmentDetails;
  if (!details) return null;

  const recommendation = details.recommendation ? RECOMMENDATION_STYLES[details.recommendation] : undefined;
  const risk = details.riskLevel ? RISK_STYLES[details.riskLevel] : undefined;

  const figures = [
    { label: 'Maximum amount', value: details.maximumAmount != null ? formatCurrency(details.maximumAmount) : undefined },
    { label: 'Interest rate', value: details.interestRate != null ? `${details.interestRate}%` : undefined },
    { label: 'Term', value: details.term != null ? `${details.term} ${details.term === 1 ? 'month' : 'months'}` : undefined }
  ].filter(figure => figure.value !== undefined);

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
        <h6 className="card-title mb-0">
          <i className="fa fa-balance-scale me-2"></i>
          Credit assessment
        </h6>
        <div className="d-flex align-items-center gap-2">
          {report?.exists && (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowReport(true)}>
              <i className="fa fa-file-text-o me-1"></i>View Credit Assessment Report
            </button>
          )}
          {risk && <span className={`badge rounded-pill ${risk.className}`}>{risk.label}</span>}
          {recommendation && (
            <span className={`badge rounded-pill ${recommendation.className}`}>{recommendation.label}</span>
          )}
        </div>
      </div>
      <div className="card-body">
        {figures.length > 0 && (
          <div className="row g-3 mb-3">
            {figures.map(figure => (
              <div key={figure.label} className="col-6 col-md-4">
                <div className="small text-muted">{figure.label}</div>
                <div className="fw-semibold">{figure.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="small text-muted mb-1">Assessment notes</div>
        {details.notes ? (
          <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{details.notes}</p>
        ) : (
          <p className="mb-0 text-muted fst-italic">No notes were recorded.</p>
        )}

        {(assessment.assessedBy || assessment.assessedDate) && (
          <div className="small text-muted mt-3">
            <i className="fa fa-user-o me-1"></i>
            {assessment.assessedBy ? `Assessed by ${assessment.assessedBy}` : 'Assessed'}
            {assessment.assessedDate && ` · ${formatDateTime(assessment.assessedDate)}`}
          </div>
        )}
      </div>
      {report?.exists && (
        <Modal show={showReport} onHide={() => setShowReport(false)} size="xl" scrollable>
          <Modal.Header closeButton><Modal.Title className="fs-6">Credit Assessment Report</Modal.Title></Modal.Header>
          <Modal.Body><CreditAssessmentReportView report={report} /></Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default CreditAssessmentSummary;
